<template>
    <div class="column" :class="[$mq, clickEnabledMulti ? 'short' : '']" :style="{ width: computedWidth }">
        <header>
            <div class="text-title">
                <span :class="['quantity', data.color]">{{ sortedTimers.length }}</span>
                <span class="text">{{ columnLabel }}</span>
            </div>
            <div v-if="clickEnabledMulti" class="btn-select-all">
                <Button @on-button-click="data.selectAll ? sendDeselectAll(data) : sendSelectAll(data)" :contentClass="data.selectAll ? 'content-title-des' : 'content-title'" :bType="data.selectAll ? 'deselect-all' : 'select-all'" :bLabel="data.selectAll ? $t('button.deselect_all') : $t('button.select_all')" customClass="selector-title" />
            </div>
        </header>
        <div class="content">
            <!-- Usar TimerGroupCard para grupos de timers (quantity > 1 o tiene groupedTimers) -->
            <template v-for="timer in sortedTimers">
                <TimerGroupCard 
                    v-if="isGroupedTimer(timer)"
                    @clickTimer="clickTimer(timer.id)" 
                    @longPress="handleLongPress(timer.id)" 
                    :visibilityType="visibilityType"
                    :showImages="showImages"
                    :key="'group-' + timer.id" 
                    :data="timer" 
                    :clickEnabledMulti="clickEnabledMulti" 
                    @addTimerSelected="addTimerSelected(timer.id, data.id)"
                />
                <!-- Mantener Timer original para timers no agrupados -->
                <Timer 
                    v-else
                    @clickTimer="clickTimer(timer.id)" 
                    @longPress="handleLongPress(timer.id)" 
                    :visibilityType="visibilityType"
                    :showImages="showImages"
                    :key="timer.id" 
                    :data="timer" 
                    :clickEnabledMulti="clickEnabledMulti" 
                    @addTimerSelected="addTimerSelected(timer.id, data.id)"
                />
            </template>
        </div>

        <footer v-if="clickEnabledMulti">
            <div class="footer-content">
                <div class="btn-delete">
                    <Button @on-button-click="deleteSelectAll()" v-if="clickEnabledMulti && data.order === 9999" contentClass="content-title" bType="delete-all" :bLabel="$t('button.delete')" customClass="selector-delete" />
                </div>
            </div>
        </footer>
    </div>
</template>

<script>
import Timer from '@/components/domain/timerproduct/timer.vue'
import TimerGroupCard from '@/components/domain/timerproduct/TimerGroupCard.vue'
import { TIMERPRODUCT_STATUS } from '@/constants'
import Button from '@/components/domain/ui/Button.vue'

export default {
    components: { Timer, TimerGroupCard, Button },
    props: {
        data: { type: Object },
        timers: { type: Array },
        visibilityType: { type: Number },
        /**
         * BUILD-7551: preferencia visual de imágenes (desde modal Cambiar vista).
         */
        showImages: { type: Boolean, default: true },
        countColumns: { type: Number },
        clickEnabledMulti: { type: Boolean }
    },
    data() {
        return {
            clickTimerIds: [],
            diffReturnValue: []
        }
    },
    computed: {
        computedWidth() {
            switch (this.countColumns) {
                case 2:
                    return '49.75%'
                case 3:
                    return '33.15%'
                case 4:
                    return '24.75%'
                default:
                    return '24.75%'
            }
        },
        sortedTimers() {
            return [...this.timers].sort((a, b) => {
                const aTime = this.getComparisonTime(a)
                const bTime = this.getComparisonTime(b)

                // Si visibilityType es 2 (timers vencidos), ordenar de mayor a menor tiempo transcurrido
                if (this.visibilityType === 2) {
                    return this.getTotalSeconds(bTime) - this.getTotalSeconds(aTime)
                }
                // Si visibilityType es 1 (timers activos), ordenar de menor a mayor tiempo restante
                else if (this.visibilityType === 1) {
                    return this.getTotalSeconds(aTime) - this.getTotalSeconds(bTime)
                }
                // Para cualquier otro caso, mantener el orden original
                return 0
            })
        },
        columnLabel() {
            if (!this.data) {
                return ''
            }
            if (this.data.id === false) {
                return this.$t('timersproduct.states.to_delete')
            }
            return this.data.name
        }
    },
    methods: {
        /**
         * Determina si un timer es un grupo (tiene múltiples timers agrupados)
         * o es un timer individual
         * @param {Object} timer - El objeto timer a evaluar
         * @returns {boolean} - true si es un grupo con más de 1 timer
         */
        isGroupedTimer(timer) {
            return timer.groupedTimers && timer.groupedTimers.length > 1
        },
        clickTimer(id) {
            this.$emit('clickTimer', id)
        },
        handleLongPress(id) {
            this.$emit('longPress', id)
        },
        addTimerSelected(id, column) {
            this.$emit('addTimerSelected', { timerId: id, columnId: column })
        },
        sendSelectAll(data) {
            if (!data) {
                return
            }
            this.$emit('selectAllTimers', { column: data })
        },
        sendDeselectAll(data) {
            this.$emit('selectAllTimers', { column: data, deselect: true })
        },
        deleteSelectAll() {
            this.$emit('deleteSelectAll', true)
        },
        getComparisonTime(timer) {
            const now = moment()
            let diffReturn = null

            // Para timers activos
            if (this.visibilityType === 1) {
                let ref_date = timer.data.date_timestamp
                if (timer.status === TIMERPRODUCT_STATUS.PREPARATION) {
                    ref_date = timer.data.prep_date_timestamp
                }

                const duration = moment.duration(moment(ref_date * 1000).diff(now))
                let diff = duration.asSeconds()

                // Calcula tiempo restante
                let diff_days = parseInt(diff / 86400)
                diff = diff - diff_days * 86400
                let diff_hours = String(parseInt(diff / 3600)).padStart(2, '0')
                diff = diff - diff_hours * 3600
                let diff_minutes = String(parseInt(diff / 60)).padStart(2, '0')
                diff = diff - diff_minutes * 60
                let diff_seconds = String(parseInt(diff)).padStart(2, '0')

                diffReturn = {
                    d: diff_days,
                    h: parseInt(diff_hours),
                    m: parseInt(diff_minutes),
                    s: parseInt(diff_seconds)
                }
            }
            // Para timers vencidos
            else {
                const ref_date = timer.data.print_date_timestamp
                const duration = moment.duration(now.diff(moment(ref_date * 1000)))
                let diff = Math.abs(duration.asSeconds())

                // Calcula tiempo transcurrido desde vencimiento
                let diff_days = parseInt(diff / 86400)
                diff = diff - diff_days * 86400
                let diff_hours = String(parseInt(diff / 3600)).padStart(2, '0')
                diff = diff - diff_hours * 3600
                let diff_minutes = String(parseInt(diff / 60)).padStart(2, '0')
                diff = diff - diff_minutes * 60
                let diff_seconds = String(parseInt(diff)).padStart(2, '0')

                diffReturn = {
                    d: diff_days,
                    h: parseInt(diff_hours),
                    m: parseInt(diff_minutes),
                    s: parseInt(diff_seconds)
                }
            }

            return diffReturn
        },
        getTotalSeconds(time) {
            if (!time) return 0
            return time.d * 86400 + time.h * 3600 + time.m * 60 + time.s
        }
    },
    created() {}
}
</script>

<style lang="scss" scoped>
.column {
    flex: 0 0 auto;
    width: 33%;
    display: inline-block;
    vertical-align: top;
    height: 100%;
    overflow: hidden;

    &:last-child {
        margin-right: 0;
    }

    header {
        @include display-flex();
        @include justify-content(space-between);
        @include align-items(center);
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
        text-align: left;
        width: 100%;
        height: 45px;
        cursor: pointer;
        padding: 5px 4px;
        text-transform: uppercase;
        background-color: #ddd;

        .text-title {
            display: flex;
            align-items: center;
            // Permite que el título se encoja y se trunque dentro del header.
            flex: 1;
            min-width: 0;
            overflow: hidden;

            .text {
                @include font-size(sm);
                font-family: $text;
                color: $neutro-s90;
                text-align: left;
                padding-left: 8px;
                // Si el nombre de la columna no cabe, se trunca con "…".
                min-width: 0;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .quantity {
                background-color: $inactive;
                padding: 6px 8px;
                text-align: center;
                font-size: 18px;
                border-radius: 4px;
                font-family: Avenir-Bold;
                background-color: $color-error-500;
                color: #fff;
                margin-right: 8px;

                &.ok {
                    background-color: $done;
                }
                &.error {
                    background-color: $color-error-500;
                }
            }
        }

        .btn-select-all {
            flex: 0 0 auto;
            width: auto;
            margin: 0;
            margin-left: auto;
            display: flex;
            justify-content: flex-end;

            ::v-deep .content-title,
            ::v-deep .content-title-des {
                width: auto;
                margin-left: 0;
                height: auto !important;

                .btn,
                .btn-select-all,
                .btn-deselect-all {
                    margin: 0 !important;
                }
            }
        }
    }

    footer {
        @include display-flex();
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
        width: 100%;
        height: 45px;
        padding: 5px;
        text-transform: uppercase;
        background-color: #ddd;

        .footer-content {
            width: 100%;
            display: flex;
            justify-content: flex-end;
            align-items: center;
        }

        .btn-delete {
            margin-top: -21px;
        }
    }

    .content {
        background-color: #fff;
        width: 100%;
        height: calc(100% - 45px);
        box-sizing: border-box;
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        // BUILD-7638: cada fila toma el alto de su tarjeta más alta y estira a la vecina.
        grid-auto-rows: auto;
        align-items: stretch;
        align-content: start;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 4px 4px 12px;
        gap: 2px;
        white-space: normal;
    }
    .content ::v-deep .timer-host {
        min-width: 0;
        align-self: stretch;
    }

    .content ::v-deep .timer,
    .content ::v-deep .timer-group {
        width: 100%;
        max-width: 100%;
        min-width: 0;
        flex: 1 0 auto;
        margin: 0;
        padding: 0;
        box-sizing: border-box;
        overflow: hidden;
    }

    &.short {
        .content {
            height: calc(100% - 90px);
        }
    }

    // --- Adaptación responsive (portrait) ---
    // En vertical la interfaz se organiza en 3 columnas y cada columna
    // muestra una única tarjeta por fila.
    &.portrait {
        // 3 columnas al ancho del board; 8px entre ellas (Figma).
        flex: 0 0 calc((100% - 16px) / 3);
        width: calc((100% - 16px) / 3) !important;
        min-width: calc((100% - 16px) / 3);
        max-width: calc((100% - 16px) / 3);

        .content {
            grid-template-columns: minmax(0, 1fr);
            // 1 tarjeta por fila: el alto sigue solo el contenido de esa tarjeta.
            align-items: start;
            padding: 2px 2px 8px;
        }

        .content ::v-deep .timer-host {
            align-self: start;
        }

        .content ::v-deep .timer,
        .content ::v-deep .timer-group {
            width: 100%;
            max-width: 100%;
            min-width: 0;
            height: auto;
            flex: 0 0 auto;
        }
    }
}
</style>
